import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tooltip,
    CircularProgress,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
} from "@mui/material";
import { X, UserPlus, Trash2, Star, StarOff } from "lucide-react";
import { toast } from "react-toastify";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import {
    fetchUserManagersAPI,
    assignManagerAPI,
    removeManagerAPI,
    setPrimaryManagerAPI,
    fetchSupervisors,
} from "../../actioncreators/actioncreators";
import { useSuspension } from "../../contexts/SuspensionContext";

/**
 * MultiManagerDialog - Dialog for managing multiple managers for a user
 *
 * Props:
 * - open: boolean - Whether the dialog is open
 * - onClose: function - Handler for closing the dialog
 * - userId: string - The user ID to manage managers for
 * - userName: string - The user's name for display
 * - orgId: string (optional) - The org to scope the manager list to. Prefer
 *   passing this from the caller (e.g. the target user's own org_id), since
 *   it's the most reliable source; falls back to Redux state below when omitted.
 * - onUpdate: function - Callback when managers are updated
 */
const MultiManagerDialog = ({ open, onClose, userId, userName, orgId: orgIdProp, onUpdate }) => {
    // Suspension check
    const { isSuspended } = useSuspension();

    // Fall back to Redux store when the caller doesn't pass orgId. state.fetchOrgId
    // is only populated as a side effect of the Members list page's fetchMembers()
    // call, so if this dialog is opened from a page reached without visiting that
    // list first (deep link, refresh, fresh session) AND no orgId prop was passed,
    // orgId came back undefined and the fetchAllPotentialManagers() guard below
    // never ran -- showing "No available managers found" even when managers exist.
    const loginResponse = useSelector((state) => state.loginResponse);
    const orgIdObj = useSelector((state) => state.fetchOrgId);
    const orgId = orgIdProp || orgIdObj?.orgId || loginResponse?.org_id || loginResponse?.orgId;

    const [managers, setManagers] = useState([]);
    const [allPotentialManagers, setAllPotentialManagers] = useState([]);
    const [availableManagers, setAvailableManagers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingManagers, setIsLoadingManagers] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedManager, setSelectedManager] = useState("");
    const [assignmentReason, setAssignmentReason] = useState("");
    const [isPrimary, setIsPrimary] = useState(false);

    // Fetch assigned managers and all potential managers when dialog opens
    useEffect(() => {
        if (open && userId && orgId) {
            fetchManagers();
            fetchAllPotentialManagers();
        }
    }, [open, userId, orgId]);

    // Filter available managers whenever assigned managers or all potential managers change
    // Only show users with LOCATION_MANAGER role
    useEffect(() => {
        if (allPotentialManagers.length > 0) {
            const assignedManagerIds = managers.map(m => m.managerId);
            const filtered = allPotentialManagers.filter(
                m => m.user_id !== userId &&
                     !assignedManagerIds.includes(m.user_id) &&
                     m.role_name === "MANAGER"
            );
            console.log("Filtered available managers:", filtered.length, "from total:", allPotentialManagers.length);
            console.log("Available managers:", filtered.map(m => ({ name: m.full_name, role: m.role_name })));
            setAvailableManagers(filtered);
        }
    }, [managers, allPotentialManagers, userId]);

    const fetchManagers = async () => {
        setIsLoading(true);
        try {
            const response = await fetchUserManagersAPI(userId);
            console.log("Fetch managers response:", response.data);
            if (response.data?.businessStatusCode === 2) {
                setManagers(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching managers:", error);
            toast.error("Failed to fetch managers");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAllPotentialManagers = async () => {
        setIsLoadingManagers(true);
        try {
            // Fetch all active users who can be managers in the same org
            const payload = {
                entity_type: "user",
                columns: ["user_id", "full_name", "email", "role_name"],
                parameter: [
                    { columnName: "status", condition: "=", value: "ACTIVE" },
                    { columnName: "org_id", condition: "=", value: orgId }
                ]
            };
            const response = await fetchSupervisors(payload);
            console.log("Fetch potential managers response:", response);
            // fetchSupervisors returns response.data directly, so access properties directly
            if (response?.businessStatusCode === 2) {
                const managerList = response?.list || [];
                console.log("Total potential managers fetched:", managerList.length);
                setAllPotentialManagers(managerList);
            } else {
                console.error("Failed to fetch managers, businessStatusCode:", response?.businessStatusCode);
            }
        } catch (error) {
            console.error("Error fetching available managers:", error);
            toast.error("Failed to load manager list");
        } finally {
            setIsLoadingManagers(false);
        }
    };

    const handleAddManager = async () => {
        if (!selectedManager) {
            toast.error("Please select a manager");
            return;
        }

        setIsAdding(true);
        try {
            const payload = {
                userId: userId,
                managerId: selectedManager,
                isPrimary: isPrimary,
                assignmentReason: assignmentReason || "Assigned via admin panel"
            };

            const response = await assignManagerAPI(payload);
            if (response.data?.businessStatusCode === 2) {
                toast.success("Manager assigned successfully");
                setShowAddForm(false);
                setSelectedManager("");
                setAssignmentReason("");
                setIsPrimary(false);
                // Refresh the managers list - available managers will auto-update via useEffect
                await fetchManagers();
                if (onUpdate) onUpdate();
            } else {
                toast.error(response.data?.message || "Failed to assign manager");
            }
        } catch (error) {
            console.error("Error assigning manager:", error);
            toast.error("Failed to assign manager");
        } finally {
            setIsAdding(false);
        }
    };

    const handleRemoveManager = async (managerId, managerName) => {
        if (!window.confirm(`Are you sure you want to remove ${managerName} as a manager?`)) {
            return;
        }

        try {
            const payload = {
                userId: userId,
                managerId: managerId,
                removeReason: "Removed via admin panel"
            };

            const response = await removeManagerAPI(payload);
            if (response.data?.businessStatusCode === 2) {
                toast.success("Manager removed successfully");
                // Refresh the managers list - available managers will auto-update via useEffect
                await fetchManagers();
                if (onUpdate) onUpdate();
            } else {
                toast.error(response.data?.message || "Failed to remove manager");
            }
        } catch (error) {
            console.error("Error removing manager:", error);
            toast.error("Failed to remove manager");
        }
    };

    const handleSetPrimary = async (managerId, managerName) => {
        try {
            const payload = {
                userId: userId,
                managerId: managerId
            };

            const response = await setPrimaryManagerAPI(payload);
            if (response.data?.businessStatusCode === 2) {
                toast.success(`${managerName} is now the primary manager`);
                fetchManagers();
                if (onUpdate) onUpdate();
            } else {
                toast.error(response.data?.message || "Failed to set primary manager");
            }
        } catch (error) {
            console.error("Error setting primary manager:", error);
            toast.error("Failed to set primary manager");
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                <Box>
                    <Typography variant="h6">Manage Managers</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {userName}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <X size={20} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Current Managers Table */}
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Assigned Managers ({managers.length})
                        </Typography>

                        {managers.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 3, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                                <Typography color="text.secondary">
                                    No managers assigned yet
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                                            <TableCell>Manager Name</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Assigned On</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {managers.map((manager) => (
                                            <TableRow key={manager.userManagerId}>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        {manager.managerName}
                                                        {manager.isPrimary && (
                                                            <Chip
                                                                label="Primary"
                                                                size="small"
                                                                color="primary"
                                                                sx={{ height: 20, fontSize: "0.7rem" }}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{manager.managerEmail}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={manager.status}
                                                        size="small"
                                                        color={manager.status === "ACTIVE" ? "success" : "default"}
                                                        sx={{ height: 20, fontSize: "0.7rem" }}
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDate(manager.effectiveFrom)}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                                                        {!manager.isPrimary && (
                                                            <Tooltip title={isSuspended ? "Actions are disabled - organization is suspended" : "Set as Primary"}>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={isSuspended ? undefined : () => handleSetPrimary(manager.managerId, manager.managerName)}
                                                                        disabled={isSuspended}
                                                                        sx={{ color: isSuspended ? "#ccc" : "#ffc107", opacity: isSuspended ? 0.5 : 1 }}
                                                                    >
                                                                        <Star size={16} />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title={isSuspended ? "Actions are disabled - organization is suspended" : "Remove Manager"}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={isSuspended ? undefined : () => handleRemoveManager(manager.managerId, manager.managerName)}
                                                                    disabled={isSuspended}
                                                                    sx={{ color: isSuspended ? "#ccc" : "#dc3545", opacity: isSuspended ? 0.5 : 1 }}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Add Manager Form */}
                        {showAddForm ? (
                            <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                    Add New Manager
                                </Typography>

                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Select Manager</InputLabel>
                                        <Select
                                            value={selectedManager}
                                            onChange={(e) => setSelectedManager(e.target.value)}
                                            label="Select Manager"
                                            disabled={isLoadingManagers}
                                        >
                                            {isLoadingManagers ? (
                                                <MenuItem disabled>Loading managers...</MenuItem>
                                            ) : availableManagers.length === 0 ? (
                                                <MenuItem disabled>No available managers found</MenuItem>
                                            ) : (
                                                availableManagers.map((mgr) => (
                                                    <MenuItem key={mgr.user_id} value={mgr.user_id}>
                                                        {mgr.full_name} ({mgr.email})
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        label="Assignment Reason (Optional)"
                                        value={assignmentReason}
                                        onChange={(e) => setAssignmentReason(e.target.value)}
                                        size="small"
                                        fullWidth
                                        placeholder="e.g., Temporary coverage during manager's leave"
                                    />

                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <input
                                            type="checkbox"
                                            id="isPrimary"
                                            checked={isPrimary}
                                            onChange={(e) => setIsPrimary(e.target.checked)}
                                        />
                                        <label htmlFor="isPrimary">Set as Primary Manager</label>
                                    </Box>

                                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                        <ArthaCancelButton
                                            onClick={() => {
                                                setShowAddForm(false);
                                                setSelectedManager("");
                                                setAssignmentReason("");
                                                setIsPrimary(false);
                                            }}
                                        >
                                            Cancel
                                        </ArthaCancelButton>
                                        <ArthaContinueButton
                                            onClick={isSuspended ? undefined : handleAddManager}
                                            disabled={isAdding || !selectedManager || isSuspended}
                                            style={isSuspended ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                                            title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                                        >
                                            {isAdding ? "Adding..." : "Add Manager"}
                                        </ArthaContinueButton>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ mt: 2 }}>
                                <ArthaContinueButton
                                    onClick={isSuspended ? undefined : () => setShowAddForm(true)}
                                    disabled={isSuspended}
                                    startIcon={<UserPlus size={16} />}
                                    style={isSuspended ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                                    title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                                >
                                    Add Manager
                                </ArthaContinueButton>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <ArthaCancelButton onClick={onClose}>
                    Close
                </ArthaCancelButton>
            </DialogActions>
        </Dialog>
    );
};

export default MultiManagerDialog;
